import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { allAPiServicesCall } from "../services/apiServices";
import { getAuthConfig } from "../services/apiUtils";

const PermissionContext = createContext();

// Permission groups for UI organization
export const PERMISSION_GROUPS = {
  artist: {
    label: "Artist",
    permissions: ["artist:view", "artist:create", "artist:edit", "artist:delete"],
  },
  song: {
    label: "Song",
    permissions: ["song:view", "song:create", "song:edit", "song:delete"],
  },
  album: {
    label: "Album",
    permissions: ["album:view", "album:create", "album:edit", "album:delete"],
  },
  playlist: {
    label: "Playlist",
    permissions: ["playlist:view", "playlist:create", "playlist:edit", "playlist:delete"],
  },
  quiz: {
    label: "Quiz",
    permissions: ["quiz:view", "quiz:create", "quiz:edit", "quiz:delete"],
  },
  genre: {
    label: "Genre",
    permissions: ["genre:view", "genre:create", "genre:edit", "genre:delete"],
  },
  news: {
    label: "News",
    permissions: ["news:view", "news:create", "news:edit", "news:delete"],
  },
  cache: {
    label: "Cache",
    permissions: ["cache:view", "cache:manage"],
  },
  contact: {
    label: "Contact",
    permissions: ["contact:view", "contact:manage"],
  },
  subscribers: {
    label: "Subscribers",
    permissions: ["subscribers:view", "subscribers:manage"],
  },
  youtube: {
    label: "YouTube",
    permissions: ["youtube:view", "youtube:manage"],
  },
  podcast: {
    label: "Podcast",
    permissions: ["podcast:view", "podcast:manage"],
  },
  songLyricsContributions: {
    label: "Song Lyrics Contributions",
    permissions: ["song-lyrics-contributions:view", "song-lyrics-contributions:manage"],
  },
  albumContributions: {
    label: "Album Contributions",
    permissions: ["album-contributions:view", "album-contributions:manage"],
  },
  songContributions: {
    label: "Song Contributions",
    permissions: ["song-contributions:view", "song-contributions:manage"],
  },
  artistBioContributions: {
    label: "Artist Bio Contributions",
    permissions: ["artist-bio-contributions:view", "artist-bio-contributions:manage"],
  },
  videoContributions: {
    label: "Video Contributions",
    permissions: ["video-contributions:view", "video-contributions:manage"],
  },
  tags: {
    label: "Tags",
    permissions: ["tags:view", "tags:manage"],
  },
  userActivity: {
    label: "User Activity",
    permissions: ["user-activity:view"],
  },
  analytics: {
    label: "Analytics",
    permissions: ["analytics:read"],
  },
  user: {
    label: "User Management",
    permissions: ["user:view", "user:edit", "user:delete"],
  },
  notification: {
    label: "Notification Management",
    permissions: ["notification:view", "notification:create", "notification:delete"],
  },
  admin: {
    label: "Admin Management",
    permissions: ["admin:view", "admin:create", "admin:edit", "admin:delete"],
  },
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState("admin");
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load admin profile on mount
  const loadAdminProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await allAPiServicesCall.getProfileDetails({}, getAuthConfig(), null);

      if (response?.success && response?.data) {
        // Handle both plain object and Mongoose document (_doc)
        const rawData = response.data;
        const userData = rawData._doc || rawData;

        setAdminData(userData);
        setPermissions(userData.permissions || []);
        setRole(userData.role || "admin");
      }
    } catch (error) {
      console.error("Failed to load admin profile:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdminProfile();
  }, [loadAdminProfile]);

  // Listen for storage changes (login/logout from other tabs or same tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'auth-token') {
        loadAdminProfile();
      }
    };

    // Also check on focus in case token was set in same tab
    const handleFocus = () => {
      const token = localStorage.getItem("auth-token");
      const currentHasToken = !!adminData;
      const newHasToken = !!token;

      // If token state changed, reload profile
      if (currentHasToken !== newHasToken || (token && !permissions.length && !loading)) {
        loadAdminProfile();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadAdminProfile, adminData, permissions, loading]);

  // Check if user has a specific permission
  const hasPermission = useCallback(
    (permission) => {
      // Superadmin has all permissions (case-insensitive)
      if (role?.toLowerCase() === "superadmin") return true;
      return permissions.includes(permission);
    },
    [permissions, role]
  );

  // Check if user has any of the given permissions
  const hasAnyPermission = useCallback(
    (permissionList) => {
      if (role?.toLowerCase() === "superadmin") return true;
      return permissionList.some((p) => permissions.includes(p));
    },
    [permissions, role]
  );

  // Check if user has all of the given permissions
  const hasAllPermissions = useCallback(
    (permissionList) => {
      if (role?.toLowerCase() === "superadmin") return true;
      return permissionList.every((p) => permissions.includes(p));
    },
    [permissions, role]
  );

  // Check if user is superadmin (case-insensitive)
  const isSuperAdmin = useCallback(() => {
    return role?.toLowerCase() === "superadmin";
  }, [role]);

  // Update permissions (after login or profile update)
  const updatePermissions = useCallback((newPermissions, newRole) => {
    setPermissions(newPermissions || []);
    if (newRole) setRole(newRole);
  }, []);

  // Refresh profile from server
  const refreshProfile = useCallback(() => {
    return loadAdminProfile();
  }, [loadAdminProfile]);

  const value = {
    permissions,
    role,
    adminData,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    updatePermissions,
    refreshProfile,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermission must be used within a PermissionProvider");
  }
  return context;
};

export default PermissionContext;
